<template>
  <SidebarLayout class="category-form mb-0" :title="$t('Categories')" :label="$t('Departments')">
    <template v-slot:buttons>
      <add-new-button @click="addNewCategory" :disabled="loading">
        <font-awesome-icon icon="plus-circle" class="icon"/>
        {{ $t('New Category') }}
      </add-new-button>
    </template>
    <template v-slot:sidebar>
      <ul class="nav flex-column">
        <li :key="department.uuid" v-for="(department) in departments"
            v-if="departments && departments.length > 0"
            :class="{ 'active': currentDepartment.uuid === department.uuid }"
            @click="changeDepartment(department)"
            class="nav-item">
          <a class="nav-link">
            {{ department.name | masterLanguage }}
          </a>
        </li>
      </ul>
    </template>
    <template v-slot:content>
      <div class="categories-tree bg-white p-2 position-relative">

        <div
          style="position: absolute;background-color: black;top:0;left: 0;bottom: 0;right: 0;z-index: 9999;opacity: 0.3;"
          v-if="loading">
          <div style="padding-top: 8em">
            <spinner show :height="10"/>
          </div>
        </div>

        <h5 v-if="currentDepartment && currentDepartment.name" style="font-size: 16px;margin-bottom: 1rem">
          {{ currentDepartment.name | masterLanguage }}
        </h5>
        <no-data-available
          v-if="currentDepartment && treeData && treeData.length === 0"
          :height="5"/>
        <tree :options="{dnd: true}"
              ref="tree"
              @node:dragging:finish="logDragFinish">
          <div class="d-flex justify-content-between" slot-scope="{ node }" style="width: 100%">
            <div class="node-text">{{ node.text }}</div>
            <div class="node-controls" style="display: inline-block; float: right">
              <a href="#" class="mr-2" @click.stop="editNode(node)">
                <font-awesome-icon :icon="'pen'"></font-awesome-icon>
              </a>
              <a href="#" class="mr-2" @click.stop="removeNode(node)">
                <font-awesome-icon :icon="'trash'"></font-awesome-icon>
              </a>
            </div>
          </div>
        </tree>
      </div>
      <category-modal/>
    </template>
  </SidebarLayout>
</template>

<script>
  import CategoryService from './CategoryService';
  import {createNamespacedHelpers} from "vuex";
  import AddNewButton from "../../../core/components/common/AddNewButton";
  import SidebarLayout from "../../../core/components/layout/DefaultLayout/SidebarLayout";
  import CategoryModal from "./CategoryModal";
  import NoDataAvailable from "../../../core/components/common/NoDataAvailable";
  import {SOURCE_CATEGORY_VIEW} from "../../constants";
  import Spinner from "@/core/components/common/Spinner";

  const {mapState: mapStateGlobal} = createNamespacedHelpers('global');
  const {mapState, mapActions, mapGetters} = createNamespacedHelpers('setup/category');
  const {mapState: mapStateDepartment} = createNamespacedHelpers('setup/department');

  export default {
    name: "Category",
    components: {
      Spinner,
      NoDataAvailable,
      CategoryModal,
      SidebarLayout,
      AddNewButton
    },
    data() {
      return {
        treeData: [],
        loading: false
      }
    },
    computed: {
      ...mapStateGlobal({
        locales: state => state.locales.available_locales
      }),
      ...mapState({
        currentDepartment: state => state.currentDepartment
      }),
      ...mapStateDepartment({
        departments: state => state.departments.rows
      }),
      ...mapGetters(['flattenedCategories']),
    },
    watch: {
      'currentDepartment.categories'() {
        this.treeData = _.cloneDeep(this.currentDepartment.categories);

        setTimeout(() => {
          let expandedNodeIds = (this.$refs.tree.findAll({state: {expanded: true}}) || []).map(node => node.id);
          this.treeData.forEach(category => CategoryService.prepareDataForTree(category, expandedNodeIds));

          let oldData = this.$refs.tree.findAll({});
          if (oldData) {
            oldData.remove();
          }
          this.treeData.forEach((cat) => {
            this.$refs.tree.append(cat);
          });
        }, 100)
      }
    },
    methods: {
      ...mapActions(['showModal', 'updateCurrentDepartment', 'removeCategory', 'updateCategory']),
      editNode(node) {
        let category = this.flattenedCategories.find((category) => category.uuid === node.id);

        // Backend returns error if we do not delete this
        delete category.parent_uuid;
        delete category.before_uuid;

        this.showModal({
          source: SOURCE_CATEGORY_VIEW,
          object: category
        });
      },
      addNewCategory() {
        this.showModal({
          source: SOURCE_CATEGORY_VIEW
        });
      },
      async removeNode(node) {
        let success = await this.$confirm(this.$i18n.t('Are you sure you want to delete following category?'));
        if (success) {
          this.loading = true
          let response = await this.removeCategory(node.id);
          this.loading = false
          if (response.success) {
            this.$toast('success', this.$i18n.t('Category was successfully deleted.'))
          } else {
            if (response.body.non_field_errors) {
              this.$errorDialog(response.body.non_field_errors[0]);
            }
          }
        }
      },
      async logDragFinish(targetNode, destinationNode, dropPosition) {
        const tmpCategory = this.flattenedCategories.find((category) => category.uuid === targetNode.id);
        this.loading = true

        if (dropPosition === 'drag-on') {
          await this.updateCategory({
            uuid: targetNode.id,
            ...this.getCategoryRequestObjectForDnd(tmpCategory.department_uuid, targetNode.parent.id, null),
          });
        } else if (dropPosition === 'drag-below' || dropPosition === 'drag-above') {

          let currentLevelState;

          if (targetNode.parent) {
            // Fires when the level of tree is anything other then first level
            currentLevelState = targetNode.parent.children;
          } else {
            // Fires when the level of tree is first
            currentLevelState = targetNode.tree.model;
          }

          let currentIndex = null;

          for (let i = 0; i < currentLevelState.length; i++) {
            if (currentLevelState[i].id === targetNode.id) {
              currentIndex = i;
              break;
            }
          }

          if (currentIndex !== (currentLevelState.length - 1)) {
            await this.updateCategory({
              uuid: targetNode.id,
              ...this.getCategoryRequestObjectForDnd(tmpCategory.department_uuid, targetNode.parent ? targetNode.parent.id : null, currentLevelState[currentIndex + 1].id),
            });
          } else {
            // TODO insert to end
            await this.updateCategory({
              uuid: targetNode.id,
              ...this.getCategoryRequestObjectForDnd(tmpCategory.department_uuid, targetNode.parent ? targetNode.parent.id : null, null),
            });
          }
        }

        this.loading = false
      },
      getCategoryRequestObjectForDnd(department_uuid, parent_uuid, before_uuid, name = null) {
        const returnObj = {};

        if (department_uuid) {
          returnObj.department_uuid = department_uuid;
        }

        if (parent_uuid) {
          returnObj.parent_uuid = parent_uuid;
        }

        if (before_uuid) {
          returnObj.before_uuid = before_uuid;
        }

        if (name) {
          returnObj.name = name;
        }

        return returnObj;
      },
      async changeDepartment(department) {
        if (this.loading) {
          return
        }
        await this.updateCurrentDepartment(department);
      },
    },
    mounted() {
      if (this.departments && this.departments.length > 0) {
        this.changeDepartment(this.departments[0]);
      }
    },
  }
</script>

<style lang="scss" scoped>
  @import "./../../../scss/index";
  @import "./scss/variables";

  .category-form {

    .form-content {
      flex: 1;
      overflow-x: hidden;
      overflow-y: auto;
    }

    .categories-tree {

      .no-data-found {
        text-align: $no-data-found-text-align;
        font-size: $no-data-found-font-size;
        margin-top: $no-data-found-margin-top;
        color: $no-data-found-color;
      }

      .tree-anchor {
        display: flex;

        > div {
          display: flex;
        }
      }

      .tree {
        height: 50vh;
      }

      /*.tree {
        .node-text {
          display: $no-data-found-tree-node-text-display;
        }

        .tree-root {
          margin: $no-data-found-tree-tree-root-margin;
          padding: $no-data-found-tree-tree-root-padding;

          .tree-content {
            border-bottom: $no-data-found-tree-tree-root-content-border-bottom;
          }
        }
      }*/
    }
  }

</style>

